import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { AiFillEye, AiFillEyeInvisible } from "react-icons/ai"; // Make sure to import icons
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { setToken, setUser } from "../../redux/userSlice.js";
import "../formStyles.css";

const Register = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation(); // Use translation hook
  const [newUser, setNewUser] = useState({
    username: "",
    phone: "", // required for HK
    code: "", // SMS OTP
    email: "", // required for account
    password: "",
    role: "",
  });
  const [smsSending, setSmsSending] = useState(false);
  const [smsVerified, setSmsVerified] = useState(false);
  const [captchaImg, setCaptchaImg] = useState("");
  const [captchaToken, setCaptchaToken] = useState("");
  const [captchaText, setCaptchaText] = useState("");
  const [cooldown, setCooldown] = useState(0);
  const [showPassword, setShowPassword] = useState(false); // State for password visibility
  const [currentStep, setCurrentStep] = useState(1); // 1: Verify phone, 2: Details

  const [emailStatus, setEmailStatus] = useState("");
  const [passwordStatus, setPasswordStatus] = useState("");
  const [formErrors, setFormErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  // Format HK phone for display: prefer "+852 XXXXXXXX"
  const formatHKPhone = (raw) => {
    if (!raw) return "";
    const digits = String(raw).replace(/\D/g, "");
    if (/^\d{8}$/.test(digits)) return `+852 ${digits}`;
    return raw;
  };

  const validateEmailFormat = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email) {
      setEmailStatus("");
      return;
    }
    if (emailRegex.test(email)) {
      setEmailStatus("");
    } else {
      setEmailStatus(t("emailInvalid"));
    }
  };

  const validatePasswordFormat = (password) => {
    const minLength = 8;
    const hasUpperCase = /[A-Z]/.test(password);
    const hasLowerCase = /[a-z]/.test(password);
    const hasNumbers = /\d/.test(password);
    const hasSpecialChars = /[!@#$%^&*]/.test(password);

    const typesCount = [
      hasUpperCase,
      hasLowerCase,
      hasNumbers,
      hasSpecialChars,
    ].filter(Boolean).length;

    if (password.length < minLength) {
      setPasswordStatus(t("passwordInvalidLength"));
    } else if (typesCount < 2) {
      setPasswordStatus(t("passwordInvalidComplexity"));
    } else {
      setPasswordStatus("");
    }
  };

  const validateForm = () => {
    const errors = {};
    let isValid = true;
    if (!newUser.username) {
      errors.username = t("register.nameRequired");
      isValid = false;
    }
    // email required
    if (!newUser.email) {
      errors.email = t("emailRequired");
      isValid = false;
    }
    // phone required and HK format
    if (!newUser.phone) {
      errors.phone = t("phoneRequired", { defaultValue: "Phone is required" });
      isValid = false;
    } else if (!/^\d{8}$/.test(newUser.phone)) {
      errors.phone = t("invalidHKPhone", {
        defaultValue: "Invalid Hong Kong phone",
      });
      isValid = false;
    }
    if (!newUser.password) {
      errors.password = t("passwordRequired");
      isValid = false;
    }
    if (emailStatus) {
      errors.email = emailStatus;
      isValid = false;
    }
    if (passwordStatus) {
      errors.password = passwordStatus;
      isValid = false;
    }
    if (!newUser.role) {
      errors.role = t("register.roleRequired");
      isValid = false;
    }
    if (!smsVerified) {
      errors.code = t("verifySmsFirst", {
        defaultValue: "Please verify SMS code",
      });
      isValid = false;
    }
    setFormErrors(errors);

    if (!isValid) {
      const errorMessage = Object.values(errors).join(" ");
      toast.error(errorMessage || t("formInvalid"));
    }

    return isValid;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setNewUser({ ...newUser, [name]: value });

    // If the phone number changes after verification, require re-verify
    if (name === "phone") {
      if (smsVerified) setSmsVerified(false);
      if (currentStep !== 1) setCurrentStep(1);
    }

    if (name === "email") {
      validateEmailFormat(value);
    }
    if (name === "password") {
      validatePasswordFormat(value);
    }

    // Reset field-specific error if it becomes valid
    if (formErrors[name]) {
      setFormErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const sendSmsCode = async () => {
    if (
      !newUser.phone ||
      (!/^\+?852[- ]?\d{8}$/.test(newUser.phone) &&
        !/^\d{8}$/.test(newUser.phone))
    ) {
      toast.error(
        t("invalidHKPhone", { defaultValue: "Invalid Hong Kong phone" })
      );
      return;
    }
    if (!captchaToken) {
      toast.error(
        t("captchaRequired", { defaultValue: "Please complete CAPTCHA" })
      );
      return;
    }
    if (!captchaText) {
      toast.error(
        t("enterCaptcha", { defaultValue: "Please enter CAPTCHA text" })
      );
      return;
    }
    setSmsSending(true);
    try {
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/sms/send_code.php`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({
            phone: newUser.phone,
            captchaToken,
            captcha: captchaText,
          }),
        }
      );
      const data = await res.json();
      if (!res.ok || !data.success) {
        if (res.status === 429 && data.retryAfter) {
          setCooldown(Number(data.retryAfter));
        }
        throw new Error(data.message || "Failed to send code");
      }
      toast.success(t("codeSent"));
      setCooldown(60);
      setCaptchaText("");
      await getCaptcha();
    } catch (e) {
      toast.error(t("errorMessage"));
    } finally {
      setSmsSending(false);
    }
  };

  const getCaptcha = async () => {
    try {
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/captcha/generate.php`,
        {
          method: "GET",
          credentials: "include",
        }
      );
      const json = await res.json();
      if (json.success) {
        setCaptchaImg(json.image);
        setCaptchaToken(json.token);
      } else {
        toast.error(
          json.message ||
            t("captchaLoadFailed", { defaultValue: "Failed to load CAPTCHA" })
        );
      }
    } catch (e) {
      toast.error(t("errorMessage"));
    }
  };

  useEffect(() => {
    getCaptcha();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (cooldown <= 0) return;
    const id = setInterval(() => setCooldown((c) => (c > 0 ? c - 1 : 0)), 1000);
    return () => clearInterval(id);
  }, [cooldown]);

  const verifySmsCode = async () => {
    if (!newUser.phone || !newUser.code) {
      toast.error(
        t("phoneAndCodeRequired", {
          defaultValue: "Phone and code are required",
        })
      );
      return;
    }
    try {
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/sms/verify_code.php`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ phone: newUser.phone, code: newUser.code }),
        }
      );
      const data = await res.json();
      if (!res.ok || !data.success)
        throw new Error(data.message || "Verify failed");
      setSmsVerified(true);
      // Auto-advance to step 2 after successful verification
      setCurrentStep(2);
      toast.success(t("verified", { defaultValue: "Verified" }));
    } catch (e) {
      setSmsVerified(false);
      toast.error(t("errorMessage"));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      return;
    }
    // Ensure verified before allowing registration
    if (!smsVerified) {
      toast.error(
        t("verifyPhoneToContinue", {
          defaultValue: "Please verify your phone number",
        })
      );
      return;
    }

    setIsLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/users.php`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify(newUser),
        }
      );
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Registration failed");
      }
      toast.success(t(data.message));
      //how to auto login use api
      if (data.success) {
        dispatch(setToken(data.data.token));
        dispatch(setUser(data.data.user));
        navigate("/profile");
      }
    } catch (err) {
      toast.error(t("errorMessage"));
      console.error("Registration failed:", err);
    } finally {
      setIsLoading(false);
    }
  };

  const getInputClass = (field) => {
    return formErrors[field] ? "input-error" : "input-default";
  };

  return (
    <div className="form-container">
      <h2>{t("register.title")}</h2>
      <form className="grid gap-4 mt-5" onSubmit={handleSubmit}>
        {/* Step indicator */}
        <div className="text-sm text-gray-600">
          {t("stepIndicator", {
            currentStep,
            defaultValue: `Step ${currentStep} / 2`,
          })}
        </div>

        {/* Step 1: Phone verification */}
        {currentStep === 1 && (
          <div className="grid gap-4">
            <div>
              <label htmlFor="phone">
                {t("phoneHK", { defaultValue: "Phone (HK)" })}:
              </label>
              <div className="flex gap-2 items-center">
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  placeholder={t("phonePlaceholder", {
                    defaultValue: "85212345678 or 12345678",
                  })}
                  className={getInputClass("phone")}
                  value={newUser.phone}
                  onChange={handleChange}
                />
              </div>
              {formErrors.phone && (
                <div className="error-message">{formErrors.phone}</div>
              )}
            </div>

            <div className="flex items-center gap-2">
              {captchaImg && (
                <img
                  src={captchaImg}
                  alt="captcha"
                  className="h-12 border rounded"
                  onClick={getCaptcha}
                  title={t("refresh", { defaultValue: "Refresh" })}
                />
              )}
              <input
                type="text"
                name="captcha"
                placeholder={t("enterCaptcha", {
                  defaultValue: "Enter CAPTCHA",
                })}
                className="input-default"
                value={captchaText}
                onChange={(e) => setCaptchaText(e.target.value)}
              />
              <button type="button" onClick={getCaptcha}>
                {t("refresh", { defaultValue: "Refresh" })}
              </button>
            </div>
            <div className="flex gap-2 items-center">
              <button
                type="button"
                disabled={smsSending || cooldown > 0}
                onClick={sendSmsCode}
              >
                {cooldown > 0
                  ? t("resendIn", {
                      seconds: cooldown,
                      defaultValue: `Resend in ${cooldown}s`,
                    })
                  : smsSending
                  ? t("sending")
                  : t("sendCode")}
              </button>
            </div>

            <div>
              <label htmlFor="code">
                {t("smsCode")}
                {":"}
              </label>
              <div className="flex gap-2 items-center">
                <input
                  type="text"
                  id="code"
                  name="code"
                  className={getInputClass("code")}
                  value={newUser.code}
                  onChange={handleChange}
                />
                <button type="button" onClick={verifySmsCode}>
                  {t("verify", { defaultValue: "Verify" })}
                </button>
                {smsVerified && (
                  <span className="text-green-600 text-sm" aria-live="polite">
                    {t("verified", { defaultValue: "Verified" })}
                  </span>
                )}
              </div>
              {formErrors.code && (
                <div className="error-message">{formErrors.code}</div>
              )}
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={() => setCurrentStep(2)}
                disabled={!smsVerified}
                className={!smsVerified ? "opacity-60 cursor-not-allowed" : ""}
                title={
                  !smsVerified
                    ? t("verifyPhoneToContinue", {
                        defaultValue: "Verify phone to continue",
                      })
                    : t("continue", { defaultValue: "Continue" })
                }
              >
                {t("continue", { defaultValue: "Continue" })}
              </button>
            </div>
          </div>
        )}

        {/* Step 2: Account details */}
        {currentStep === 2 && (
          <div className="grid gap-4">
            <div className="flex  flex-col items-center">
              <div className="text-sm text-green-700">
                {t("phoneVerified", { defaultValue: "Phone verified" })}:{" "}
                {formatHKPhone(newUser.phone)}
              </div>
              <button type="button" onClick={() => setCurrentStep(1)}>
                {t("back", { defaultValue: "Back" })}
              </button>
            </div>

            <div>
              <label htmlFor="email">{t("register.email")}:</label>
              <input
                type="email"
                id="email"
                name="email"
                required
                className={getInputClass("email")}
                value={newUser.email}
                onChange={handleChange}
              />
              {formErrors.email ? (
                <div className="error-message">{formErrors.email}</div>
              ) : (
                emailStatus && (
                  <div className="error-message">{emailStatus}</div>
                )
              )}
            </div>

            <div className="flex flex-col gap-1 relative">
              <label htmlFor="password">{t("register.password")}:</label>
              <div
                className="cursor-pointer absolute  right-2 top-5"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? (
                  <AiFillEyeInvisible size={20} />
                ) : (
                  <AiFillEye size={20} />
                )}
              </div>
              <input
                type={showPassword ? "text" : "password"}
                id="password"
                name="password"
                className={getInputClass("password")}
                value={newUser.password}
                onChange={handleChange}
              />
              {passwordStatus && (
                <div className="error-message">{passwordStatus}</div>
              )}
            </div>

            <div>
              <label htmlFor="username">{t("register.username")}:</label>
              <input
                type="text"
                id="username"
                name="username"
                className={getInputClass("username")}
                value={newUser.username}
                onChange={handleChange}
              />
              {formErrors.username && (
                <div className="error-message">{formErrors.username}</div>
              )}
            </div>

            <div>
              <label htmlFor="role">{t("register.role")}:</label>
              <div className="form-selector">
                <div className="  m-1">
                  <span
                    onClick={() => setNewUser({ ...newUser, role: "student" })}
                    className={`form-option ${
                      newUser.role === "student" ? "selected" : ""
                    }`}
                  >
                    {t("student")}
                  </span>
                  <span
                    onClick={() => setNewUser({ ...newUser, role: "tutor" })}
                    className={`form-option ${
                      newUser.role === "tutor" ? "selected" : ""
                    }`}
                  >
                    {t("tutor")}
                  </span>
                </div>
                {formErrors.role && (
                  <div className="error-message">{formErrors.role}</div>
                )}
              </div>
            </div>

            <button type="submit" disabled={isLoading || !smsVerified}>
              {isLoading ? t("register.loading") : t("register.submit")}
            </button>
          </div>
        )}
      </form>
    </div>
  );
};

export default Register;
